import { useCallback, useEffect, useMemo } from "react";

import { useUserAvailableAssets } from "@bridgesplit/abf-react";
import { TokenPositionExpanded } from "@bridgesplit/abf-sdk";
import { lamportsToUiAmount, Result, roundToDecimals, uiAmountToLamports } from "@bridgesplit/utils";
import { useBalanceChecker } from "app/components/common";
import BN from "bn.js";

import { AUTO_SLIPPAGE_DECIMALS, useRaydiumContext } from "../RaydiumContext";
import { getAddLiquidityQuote } from "../raydium";
import { useRaydiumLtv } from "../util";

export function useDepositRaydiumBalances() {
    const {
        depositForm,
        raydiumPosition: { tokenA, tokenB },
        slippageController: { slippagePercentDecimals }
    } = useRaydiumContext();

    const { data } = useUserAvailableAssets({});

    const getBalance = useCallback(
        (token: TokenPositionExpanded) => {
            if (!data) return undefined;

            const userBalance = data.find((d) => d.metadata.mint === token.mint)?.amount ?? 0;

            const slippage = (slippagePercentDecimals ?? AUTO_SLIPPAGE_DECIMALS) * userBalance;
            return roundToDecimals(userBalance - slippage, token.decimals);
        },
        [data, slippagePercentDecimals]
    );

    const A = useBalanceChecker({
        mint: tokenA.metadata.mint,
        amount: depositForm.tokenAAmount,
        customBalance: getBalance(tokenA)
    });

    const B = useBalanceChecker({
        mint: tokenB.metadata.mint,
        customBalance: getBalance(tokenB),
        amount: depositForm.tokenBAmount
    });

    return { A, B };
}

export function useUpdateDepositAmount() {
    const { setDepositForm } = useRaydiumContext();

    return useCallback(
        (amount: number | undefined, side: "A" | "B") => {
            setDepositForm((prev) => {
                const tokenAmountKey = side === "A" ? "tokenAAmount" : "tokenBAmount";
                if (prev[tokenAmountKey] === amount) return prev;
                return { ...prev, [tokenAmountKey]: amount, side, status: "refetch-needed" };
            });
        },
        [setDepositForm]
    );
}

export function useRaydiumDepositHooks() {
    useMaintainDepositQuote();
}

function useMaintainDepositQuote() {
    const {
        raydiumPosition,
        poolData,
        depositForm,
        raydiumOffchain,
        setDepositForm,
        setAddLiquidityQuote,
        slippageController: { slippagePercentDecimals }
    } = useRaydiumContext();

    const fetchYieldData = useCallback(async () => {
        try {
            if (!poolData || !raydiumOffchain) return;

            const { tokenA, tokenB } = raydiumPosition;

            const side = (() => {
                if (depositForm.side) return depositForm.side;

                // pick the side that has more balance if side is not set
                const percentOfAMax = (depositForm.tokenAAmount ?? 0) / tokenA.amount;
                const percentOfBMax = (depositForm.tokenBAmount ?? 0) / tokenB.amount;
                return percentOfAMax > percentOfBMax ? "A" : "B";
            })();
            const token = side === "A" ? tokenA : tokenB;
            const amount = side === "A" ? depositForm.tokenAAmount : depositForm.tokenBAmount;
            if (amount === undefined) return;

            setDepositForm((prev) => ({
                ...prev,
                status: prev.status === "silent-refetch" ? prev.status : "refetching"
            }));

            const quote = getAddLiquidityQuote(raydiumPosition, {
                positionAddress: raydiumPosition.position.nftMint,
                tokenMint: token.mint,
                tokenAmount: new BN(uiAmountToLamports(amount, token.decimals)),
                refresh: false,
                slippageTolerance: slippagePercentDecimals ?? AUTO_SLIPPAGE_DECIMALS
            });

            const tokenAAmount = lamportsToUiAmount(quote.estTokenA.toNumber(), tokenA.decimals);
            const tokenBAmount = lamportsToUiAmount(quote.estTokenB.toNumber(), tokenB.decimals);

            const maxTokenAAmount = lamportsToUiAmount(quote.maxTokenA.toNumber(), tokenA.decimals);
            const maxTokenBAmount = lamportsToUiAmount(quote.maxTokenB.toNumber(), tokenB.decimals);

            setDepositForm((prev) => ({
                ...prev,
                tokenAAmount: side === "A" ? prev.tokenAAmount : tokenAAmount,
                tokenBAmount: side === "B" ? prev.tokenBAmount : tokenBAmount,
                maxTokenAAmount: maxTokenAAmount,
                maxTokenBAmount: maxTokenBAmount,
                status: "updated"
            }));
            setAddLiquidityQuote(quote);
        } catch (error) {
            Result.err(error);
            setDepositForm((prev) => ({ ...prev, status: "error" }));
        }
    }, [
        depositForm.side,
        depositForm.tokenAAmount,
        depositForm.tokenBAmount,
        poolData,
        raydiumOffchain,
        raydiumPosition,
        setAddLiquidityQuote,
        setDepositForm,
        slippagePercentDecimals
    ]);

    useEffect(() => {
        if (depositForm.status && ["refetch-needed", "silent-refetch"].includes(depositForm.status)) {
            fetchYieldData();
        }
    }, [fetchYieldData, depositForm.status]);

    return { fetchYieldData };
}

export function useDepositLtv() {
    const rp = useRaydiumUsdValue();
    return useRaydiumLtv(rp?.positionUsdValue);
}

function useRaydiumUsdValue() {
    const {
        depositForm,
        raydiumPosition: { tokenA, tokenB }
    } = useRaydiumContext();
    return useMemo(() => {
        const tokenAUsd = ((depositForm?.tokenAAmount ?? 0) + tokenA.amount) * tokenA.usdPrice;
        const tokenBUsd = ((depositForm?.tokenBAmount ?? 0) + tokenB.amount) * tokenB.usdPrice;

        const positionUsdValue = tokenAUsd + tokenBUsd;
        return { positionUsdValue, tokenAUsd, tokenBUsd };
    }, [depositForm, tokenA, tokenB]);
}
